import { CustomScriptPlacement } from "@/cms/models";
import { decodeCustomScript } from "@/utils/custom-scripts";

/**
 * The DOM half of the third-party script feature, deliberately kept free of React so the
 * mechanism can be exercised directly in a real browser (see
 * tests/custom-scripts-injection.spec.ts). CustomScripts.tsx is only a useEffect wrapper
 * around injectCustomScript.
 *
 * Everything here runs in the browser: the snippet is customer code that must never execute
 * server-side, and injecting it client-side keeps it out of the server-rendered HTML that is
 * cached and shared.
 */

/**
 * Converts a node parsed out of innerHTML into one the browser will actually run.
 *
 * A <script> element created by the HTML parser from innerHTML is marked "already started"
 * and never executes, no matter where it is inserted. The only way to get it to run is to
 * build a fresh element with document.createElement and copy the attributes and the inline
 * source across. Wrapper elements are rebuilt the same way so a script nested inside a <div>
 * is re-created too; everything without a script inside it is imported as-is.
 */
export function toExecutableNode(node: Node): Node {
  if (node.nodeName === "SCRIPT") {
    const source = node as HTMLScriptElement;
    const script = document.createElement("script");
    for (const attribute of Array.from(source.attributes)) {
      script.setAttribute(attribute.name, attribute.value);
    }
    script.textContent = source.textContent;
    return script;
  }

  if (
    node.nodeType === Node.ELEMENT_NODE &&
    (node as Element).querySelector("script") != null
  ) {
    const wrapper = document.importNode(node, false);
    for (const child of Array.from(node.childNodes)) {
      wrapper.appendChild(toExecutableNode(child));
    }
    return wrapper;
  }

  return document.importNode(node, true);
}

/**
 * Decodes one stored snippet and inserts it at its placement, returning the cleanup that
 * removes exactly what was inserted. Returns undefined when there is nothing to inject.
 *
 * Insertion is anchored to the document rather than to any position in the React tree, so
 * "before </body>" is literally the last child of <body> — which is what third-party chat and
 * widget scripts generally require.
 */
export function injectCustomScript(
  code: string,
  placement: CustomScriptPlacement,
): (() => void) | undefined {
  const decoded = decodeCustomScript(code);
  if (decoded.trim().length === 0) {
    return undefined;
  }

  const template = document.createElement("template");
  template.innerHTML = decoded;

  const fragment = document.createDocumentFragment();
  for (const child of Array.from(template.content.childNodes)) {
    fragment.appendChild(toExecutableNode(child));
  }

  if (fragment.childNodes.length === 0) {
    return undefined;
  }

  // Inserting the fragment empties it, so snapshot the top-level nodes first; the cleanup
  // below needs them to remove exactly what was added and not leave duplicates behind on a
  // re-render or unmount.
  const insertedNodes = Array.from(fragment.childNodes);

  // A single fragment insert preserves the snippet's own source order.
  if (placement === "head") {
    document.head.appendChild(fragment);
  } else if (placement === "bodyStart") {
    document.body.insertBefore(fragment, document.body.firstChild);
  } else {
    document.body.appendChild(fragment);
  }

  return () => {
    for (const node of insertedNodes) {
      node.parentNode?.removeChild(node);
    }
  };
}
